import { useCallback, useEffect, useState } from 'react';
import { type AdminInquiry, deleteInquiry, fetchInquiryList } from './adminInquiryApi';
import './AdminInquiryManage.scss';

function formatDateTime(value?: string | null) {
  if (!value) return '—';
  const normalized = String(value).replace(' ', 'T');
  const date = new Date(normalized);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleString('ko-KR', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export default function AdminInquiryManage() {
  const [inquiries, setInquiries] = useState<AdminInquiry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [appliedSearch, setAppliedSearch] = useState('');
  const [deletingId, setDeletingId] = useState<number | null>(null);

  const loadInquiries = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const list = await fetchInquiryList({
        q: appliedSearch || undefined,
      });
      setInquiries(list);
    } catch (err) {
      setInquiries([]);
      setError(err instanceof Error ? err.message : '문의 목록을 불러오지 못했습니다.');
    } finally {
      setLoading(false);
    }
  }, [appliedSearch]);

  useEffect(() => {
    void loadInquiries();
  }, [loadInquiries]);

  const handleSearch = () => {
    setAppliedSearch(searchQuery.trim());
  };

  const handleDelete = async (row: AdminInquiry) => {
    const ok = window.confirm(`「${row.name}」 님의 문의를 삭제하시겠습니까?`);
    if (!ok) return;

    setDeletingId(row.id);
    setError(null);
    try {
      await deleteInquiry(row.id);
      setInquiries((prev) => prev.filter((item) => item.id !== row.id));
    } catch (err) {
      const message = err instanceof Error ? err.message : '문의 삭제에 실패했습니다.';
      setError(message);
      alert(message);
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <div className="admin-inquiry-manage">
      <header className="admin-inquiry-manage__header">
        <div>
          <h1 className="admin-inquiry-manage__title">문의 접수 목록</h1>
          <p className="admin-inquiry-manage__subtitle">
            홈페이지 문의하기 폼에서 접수된 제작 의뢰 내역입니다.
          </p>
        </div>
        <button
          type="button"
          className="admin-inquiry-manage__refresh-btn"
          onClick={() => void loadInquiries()}
          disabled={loading}
        >
          새로고침
        </button>
      </header>

      <div className="admin-inquiry-manage__toolbar">
        <input
          className="admin-inquiry-manage__search"
          type="search"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') handleSearch();
          }}
          placeholder="이름, 이메일, 연락처, 회사명, 카테고리, 내용 검색"
        />
        <button
          type="button"
          className="admin-inquiry-manage__search-btn"
          onClick={handleSearch}
          disabled={loading}
        >
          검색
        </button>
      </div>

      {error && <p className="admin-inquiry-manage__error">{error}</p>}

      <p className="admin-inquiry-manage__count">총 {inquiries.length}건</p>

      {loading ? (
        <p className="admin-inquiry-manage__loading">불러오는 중…</p>
      ) : inquiries.length === 0 ? (
        <p className="admin-inquiry-manage__empty">등록된 문의가 없습니다.</p>
      ) : (
        <div className="admin-inquiry-manage__table-wrap">
          <table className="admin-inquiry-manage__table">
            <thead>
              <tr>
                <th>접수일</th>
                <th>이름</th>
                <th>회사명</th>
                <th>이메일</th>
                <th>연락처</th>
                <th>카테고리</th>
                <th>예산</th>
                <th>기획 상태</th>
                <th>상세 내용</th>
                <th>관리</th>
              </tr>
            </thead>
            <tbody>
              {inquiries.map((row) => (
                <tr key={row.id}>
                  <td>{formatDateTime(row.created_at)}</td>
                  <td>{row.name}</td>
                  <td>{row.company || '—'}</td>
                  <td>
                    <a className="admin-inquiry-manage__link" href={`mailto:${row.email}`}>
                      {row.email}
                    </a>
                  </td>
                  <td>
                    <a className="admin-inquiry-manage__link" href={`tel:${row.phone}`}>
                      {row.phone}
                    </a>
                  </td>
                  <td>{row.category || '—'}</td>
                  <td>{row.budget || '—'}</td>
                  <td>{row.planning_status || '—'}</td>
                  <td className="admin-inquiry-manage__message">{row.message}</td>
                  <td>
                    <button
                      type="button"
                      className="admin-inquiry-manage__delete-btn"
                      onClick={() => void handleDelete(row)}
                      disabled={deletingId === row.id}
                    >
                      {deletingId === row.id ? '삭제 중' : '삭제'}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
